import styled from '@emotion/styled';
import { Alert, Text, tokens } from '@hotjar/hotsauce';

type BackgroundColors = 'lilac' | 'magenta';

const Wrapper = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  min-width: 365px;
  min-height: 240px;
  box-sizing: content-box;
  overflow: hidden;
  background-color: ${tokens.color.ui_background};
  border: 1px solid ${tokens.color.ui_border};
  border-radius: ${tokens.spacer.x1};

  &:hover {
    background-color: ${tokens.color.bg_hover};
  }
`;

const StyledAlert = styled(Alert)`
  position: absolute;
  align-items: center;
  gap: ${tokens.spacer.x1pt5};
  bottom: 0;
  left: -1px;
  width: fit-content;
  height: 28px;
  min-height: 0;
  padding: 6px ${tokens.spacer.x1pt5};
  z-index: 2;
  border-radius: 0;
  overflow: hidden;

  svg {
    width: 14px;
    min-width: 0;
    height: 14px;
    min-height: 0;
  }

  p {
    ${tokens.text('caption')}
    margin-top: 0;
    color: ${tokens.color.text_light};
    white-space: nowrap;
  }

  svg {
    margin-top: 2px;
  }
`;

const ImageWrapper = styled.div<{ backgroundColor: BackgroundColors }>`
  position: relative;
  display: flex;
  z-index: 0;
  justify-content: center;
  align-items: center;
  width: 100%;
  min-height: 150px;
  overflow: hidden;
  padding: 20px;
  background-color: ${({ backgroundColor }) =>
    backgroundColor === 'magenta' ? `${tokens.color.magenta_100}` : `${tokens.color.lilac_100}`};

  &:before {
    content: '';
    position: absolute;
    top: 0;
    left: -105px;
    z-index: 0;
    min-width: 359px;
    min-height: 308px;
    background: ${({ backgroundColor }) =>
      backgroundColor === 'lilac'
        ? 'radial-gradient(122.33% 145.31% at 34.58% 107.85%, #E4DFF3 0%, #F3F1FA 47.4%)'
        : 'radial-gradient(122.33% 145.31% at 34.58% 107.85%, #ffb49d 0%, #ffece6 47.4%)'};
    filter: blur(65.2238px);
    transform: matrix(0.77, 0.5, -0.76, 0.8, 0, 0);
  }
`;

const Image = styled.img`
  z-index: 1;
  max-width: 100%;
  max-height: 100%;
  box-shadow: 0px 2px 8px rgba(0, 0, 0, 0.12);
  border-radius: 2px;
  object-fit: cover;
`;

const ContentWrapper = styled.div`
  padding: 12px 16px;
`;

const Link = styled.a`
  display: block;
  width: 100%;
  height: 100%;

  :hover {
    text-decoration: none;
  }
`;

const Title = styled(Text)`
  display: block;
  margin-bottom: 5px;
`;

const Description = styled(Text)`
  display: block;
`;

export interface QuickLinkCardProps {
  backgroundColor?: BackgroundColors;
  className?: string;
  description: string;
  href: string;
  image: string;
  isExternalLink?: boolean;
  onClick: () => void;
  title: string;
  alert?: string;
}

export const QuickLinkCard = ({
  backgroundColor = 'lilac',
  className,
  description,
  href,
  image,
  isExternalLink,
  onClick,
  title,
  alert,
}: QuickLinkCardProps): JSX.Element => {
  return (
    <Wrapper className={className}>
      <Link
        href={href}
        onClick={onClick}
        data-testid="quick-link-card"
        aria-label={title}
        aria-details={description}
        target={isExternalLink ?? false ? '_blank' : '_self'}
      >
        <ImageWrapper backgroundColor={backgroundColor}>
          {alert !== undefined ? <StyledAlert kind="warning">{alert}</StyledAlert> : null}
          <Image src={image} alt="title" aria-hidden />
        </ImageWrapper>
        <ContentWrapper>
          <Title as="span" textType="subtitle1">
            {title}
          </Title>
          <Description as="span" textType="body2" textColor="text_light">
            {description}
          </Description>
        </ContentWrapper>
      </Link>
    </Wrapper>
  );
};
